import { z } from "zod";
import { projectIdSchema, recordSchema, metadataSchema } from "./index.js";
import { count } from "./scene.js";
export const READ_LIMITS = {
  requestMs: 5000,
  records: 100_000,
  bytes: 256 * 1024 * 1024,
  batches: 10_000,
  responseBytes: 2 * 1024 * 1024,
} as const;
export const projectSchema = z.strictObject({
  id: projectIdSchema,
  name: z.string().min(1).max(128),
});
export type Project = z.infer<typeof projectSchema>;
const integer = (max: number) =>
  z
    .string()
    .regex(/^(0|[1-9]\d*)$/)
    .transform(Number)
    .pipe(z.number().int().min(0).max(max));
export const listQuerySchema = z
  .strictObject({
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    gameVersion: z.string().min(1).max(128).optional(),
    unitId: z.string().min(1).max(128).optional(),
    sessionId: z.uuid().optional(),
    attemptId: z.uuid().optional(),
    outcome: z
      .enum(["success", "failure", "draw", "none", "aborted", "unknown"])
      .optional(),
    sort: z
      .enum([
        "sessionStartedAt",
        "durationMs",
        "clearedCount",
        "attemptCount",
        "entryUnit",
        "lastUnit",
        "unitId",
        "sessionCount",
        "successRate",
        "medianMs",
        "exitCount",
        "exitRate",
      ])
      .optional(),
    order: z.enum(["asc", "desc"]).optional(),
    entryUnit: z.string().max(128).optional(),
    hasEntry: z.enum(["yes", "no"]).optional(),
    lastUnit: z.string().max(128).optional(),
    durationMin: integer(Number.MAX_SAFE_INTEGER).optional(),
    durationMax: integer(Number.MAX_SAFE_INTEGER).optional(),
    clearedCount: integer(100000).optional(),
    activity: z.enum(["active", "inactive"]).optional(),
    page: integer(1e6).pipe(z.number().min(1)).default(1),
    pageSize: z.enum(["10", "20", "50", "100"]).transform(Number).default(20),
  })
  .refine((q) => !q.from || !q.to || Date.parse(q.from) <= Date.parse(q.to))
  .refine(
    (q) =>
      q.durationMin === undefined ||
      q.durationMax === undefined ||
      q.durationMin < q.durationMax,
  );
export type ListQuery = z.infer<typeof listQuerySchema>;
export const segmentQuerySchema = z.strictObject({
  afterSeq: integer(Number.MAX_SAFE_INTEGER).default(0),
  limit: integer(100).pipe(z.number().min(1)).default(50),
  kind: z.enum(["all", "event", "snapshot"]).default("all"),
});
export const positionQuerySchema = z.strictObject({
  sessionMs: integer(Number.MAX_SAFE_INTEGER),
  seq: integer(Number.MAX_SAFE_INTEGER).default(Number.MAX_SAFE_INTEGER),
  direction: z.enum(["at", "previous", "next"]).default("at"),
});
const range = z.tuple([count, count]);
export const qualitySchema = z.strictObject({
  scope: z.literal("session"),
  status: z.enum([
    "complete",
    "tail_unknown",
    "pending",
    "dropped",
    "truncated",
  ]),
  missingRanges: z.array(range).max(256),
  droppedRanges: z.array(range).max(256),
  rangesTruncated: z.boolean(),
});
const outcome = z.enum([
  "success",
  "failure",
  "draw",
  "none",
  "aborted",
  "unknown",
]);
export const attemptSummarySchema = z.strictObject({
  projectId: projectIdSchema,
  sessionId: z.uuid(),
  attemptId: z.uuid(),
  unitId: z.string(),
  gameVersion: z.string(),
  sessionStartedAt: z.iso.datetime(),
  startSessionMs: count,
  startSeq: count,
  hasStart: z.boolean(),
  startReason: z.enum(["initial", "retry", "next"]).nullable(),
  previousAttemptId: z.uuid().nullable(),
  endSessionMs: count.nullable(),
  outcome,
  firstMs: count,
  lastMs: count,
  elapsedMs: count.nullable(),
  activeMs: count.nullable(),
  eventCount: count,
  snapshotCount: count,
  quality: qualitySchema,
});
export type AttemptSummary = z.infer<typeof attemptSummarySchema>;
export const sessionSourceSchema = z.strictObject({
  ip: z.union([z.ipv4(), z.ipv6()]).nullable(),
  countryCode: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .nullable(),
  provider: z.literal("cloudflare").nullable(),
});
export type SessionSource = z.infer<typeof sessionSourceSchema>;
export const sessionSummarySchema = metadataSchema.extend({
  source: sessionSourceSchema.nullable().optional(),
  rating: z.number().int().min(1).max(5).nullable().optional(),
  firstMs: count,
  lastMs: count,
  attemptCount: count,
  knownActiveMs: count,
  activeSampleCount: count,
  activeComplete: z.boolean(),
  eventCount: count,
  snapshotCount: count,
  quality: qualitySchema,
});
export type SessionSummary = z.infer<typeof sessionSummarySchema>;
export const readMetaSchema = z.strictObject({
  readAt: z.iso.datetime(),
  consistency: z.enum(["live", "eventual"]),
  requestId: z.string(),
});
export const projectsResponseSchema = readMetaSchema.extend({
  projects: z.array(
    projectSchema.extend({
      capabilities: z.strictObject({
        statistics: z.literal(true),
        replay: z.boolean(),
      }),
    }),
  ),
});
const page = { total: count, page: count, pageSize: count };
export const listResponseSchema = readMetaSchema.extend({
  ...page,
  items: z.array(attemptSummarySchema),
});
export const sessionsResponseSchema = readMetaSchema.extend({
  ...page,
  items: z.array(sessionSummarySchema),
});
export const detailResponseSchema = readMetaSchema.extend({
  item: sessionSummarySchema,
});
export const contextSchema = z.strictObject({
  paused: z.boolean().nullable(),
  visibility: z.enum(["visible", "hidden"]).nullable(),
  attemptId: z.string().nullable(),
  known: z.boolean(),
});
export const segmentResponseSchema = readMetaSchema.extend({
  records: z.array(recordSchema),
  nextAfterSeq: count.nullable(),
  hasMore: z.boolean(),
});
export const positionResponseSchema = readMetaSchema.extend({
  positionSeq: count,
  snapshot: recordSchema.nullable(),
  environment: recordSchema.nullable(),
  context: contextSchema,
});
export const recordResponseSchema = readMetaSchema.extend({
  record: recordSchema,
});
export const statsResponseSchema = readMetaSchema.extend({
  sessionCount: count,
  attemptCount: count,
  outcomes: z.strictObject({
    success: count,
    failure: count,
    draw: count,
    none: count,
    aborted: count,
    unknown: count,
  }),
  clearedRate: z.number().nullable(),
  meanActiveMs: z.number().nullable(),
  activeSampleCount: count,
});
export const identifierSchema = z.uuid();
export const emptyQuerySchema = z.strictObject({});
export const readErrorResponseSchema = z.strictObject({
  requestId: z.string(),
  error: z.strictObject({ code: z.string(), message: z.string() }),
});

export const ANALYSIS_IDLE_MS = 5 * 60_000;
// Analysis responses tolerate additive fields at every object boundary.
// Derive known fields from the strict contracts; upload/request validation stays strict.
export const analysisSessionSchema = z.object({
  ...sessionSummarySchema.shape,
  capabilities: z.object(metadataSchema.shape.capabilities.shape),
  presentation: z
    .object(metadataSchema.shape.presentation.unwrap().shape)
    .nullable(),
  source: z.object(sessionSourceSchema.shape).nullable().optional(),
  quality: z.object(qualitySchema.shape),
  durationMs: count,
  clearedCount: count,
  entryUnit: z.string().nullable(),
  lastUnit: z.string().nullable(),
  entryKnown: z.boolean(),
  lastOutcome: outcome.nullable(),
  lastReceivedAt: z.iso.datetime(),
  analyzedAt: z.iso.datetime(),
});
export type AnalysisSession = z.infer<typeof analysisSessionSchema>;
export const unitAnalysisSchema = z.object({
  unitId: z.string(),
  sessionCount: count,
  attemptCount: count,
  successCount: count,
  successRate: z.number(),
  medianMs: z.number().nullable(),
  durationSampleCount: count,
  eligibleSessionCount: count,
  exitCount: count,
  exitRate: z.number().nullable(),
  successExits: count,
  failureExits: count,
  abortedExits: count,
  unknownExits: count,
});
export type UnitAnalysis = z.infer<typeof unitAnalysisSchema>;
const bucketSchema = z.object({
  key: z.string(),
  label: z.string(),
  count,
  min: count.optional(),
  max: count.optional(),
});
export const analysisResponseSchema = z.object({
  requestId: z.string(),
  readAt: z.iso.datetime(),
  consistency: z.literal("eventual"),
  analyzedAt: z.iso.datetime().nullable(),
  idleMs: count,
  total: count,
  page: count,
  pageSize: count,
  sessions: z.array(analysisSessionSchema),
  units: z.array(unitAnalysisSchema),
  overview: z.object({
    sessionCount: count,
    enteredCount: count,
    medianDurationMs: z.number().nullable(),
    meanClearedCount: z.number().nullable(),
    attemptCount: count,
    activeSessionCount: count,
    tailUnknownCount: count,
  }),
  durations: z.array(bucketSchema),
  clears: z.array(bucketSchema),
  entries: z.array(bucketSchema),
  countries: z.array(bucketSchema).max(50).optional(),
  ratings: z.array(bucketSchema).max(5).optional(),
  unknownCountryCount: count.optional(),
  unratedCount: count.optional(),
});
export type AnalysisResponse = z.infer<typeof analysisResponseSchema>;
export const analysisOptionsResponseSchema = z.object({
  ...readMetaSchema.shape,
  consistency: z.literal("eventual"),
  unitIds: z.array(z.string().min(1).max(128)).max(1000),
});
export type AnalysisOptionsResponse = z.infer<
  typeof analysisOptionsResponseSchema
>;
